import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DailyDigestView = ({ tasks }) => {
  const today = new Date();
  const todayStr = today?.toISOString()?.split('T')?.[0];

  const digestData = useMemo(() => {
    const overdue = tasks?.filter(t => {
      if (!t?.due_date || t?.status === 'completed') return false;
      const dueDate = new Date(t?.due_date)?.toISOString()?.split('T')?.[0];
      return dueDate < todayStr;
    });

    const dueToday = tasks?.filter(t => {
      if (!t?.due_date) return false;
      const dueDate = new Date(t?.due_date)?.toISOString()?.split('T')?.[0];
      return dueDate === todayStr;
    });

    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    const tomorrowStr = tomorrow?.toISOString()?.split('T')?.[0];

    const dueTomorrow = tasks?.filter(t => {
      if (!t?.due_date) return false;
      const dueDate = new Date(t?.due_date)?.toISOString()?.split('T')?.[0];
      return dueDate === tomorrowStr;
    });

    const thisWeek = tasks?.filter(t => {
      if (!t?.due_date) return false;
      const dueDate = new Date(t?.due_date);
      const weekFromNow = new Date(today);
      weekFromNow?.setDate(weekFromNow?.getDate() + 7);
      return dueDate > tomorrow && dueDate <= weekFromNow;
    });

    const completed = tasks?.filter(t => {
      if (t?.status !== 'completed' || !t?.completed_at) return false;
      const completedDate = new Date(t?.completed_at)?.toISOString()?.split('T')?.[0];
      return completedDate === todayStr;
    });

    const inProgress = tasks?.filter(t => t?.status === 'in_progress');

    return {
      overdue,
      dueToday,
      dueTomorrow,
      thisWeek,
      completed,
      inProgress
    };
  }, [tasks, todayStr]);

  const getPriorityIcon = (priority) => {
    const icons = {
      urgent: 'AlertCircle',
      high: 'ArrowUp',
      normal: 'Minus',
      low: 'ArrowDown'
    };
    return icons?.[priority] || 'Minus';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'var(--color-error)',
      high: 'var(--color-warning)',
      normal: 'var(--color-primary)',
      low: 'var(--color-muted-foreground)'
    };
    return colors?.[priority] || colors?.normal;
  };

  const formatDate = () => {
    return today?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const TaskList = ({ tasks, emptyMessage }) => {
    if (tasks?.length === 0) {
      return (
        <p className="text-sm text-muted-foreground italic py-2">{emptyMessage}</p>
      );
    }

    return (
      <div className="space-y-2">
        {tasks?.map((task) => (
          <div
            key={task?.id}
            className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
          >
            <Icon
              name={getPriorityIcon(task?.priority)}
              size={16}
              color={getPriorityColor(task?.priority)}
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{task?.title}</p>
              {task?.description && (
                <p className="text-xs text-muted-foreground mt-1">{task?.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-success/10 rounded-lg border border-primary/20 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-1">
              Daily Productivity Digest
            </h2>
            <p className="text-muted-foreground">{formatDate()}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            className="flex items-center gap-2"
          >
            <Icon name="Printer" size={16} />
            Print
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={18} color="var(--color-error)" />
            <p className="text-sm font-medium text-foreground">Overdue</p>
          </div>
          <p className="text-3xl font-semibold text-error">{digestData?.overdue?.length}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={18} color="var(--color-warning)" />
            <p className="text-sm font-medium text-foreground">Due Today</p>
          </div>
          <p className="text-3xl font-semibold text-warning">{digestData?.dueToday?.length}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
            <p className="text-sm font-medium text-foreground">Completed Today</p>
          </div>
          <p className="text-3xl font-semibold text-success">{digestData?.completed?.length}</p>
        </div>
      </div>

      {/* Overdue Tasks */}
      {digestData?.overdue?.length > 0 && (
        <div className="bg-card rounded-lg border border-error/20 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <h3 className="text-lg font-semibold text-foreground">
              Overdue Tasks ({digestData?.overdue?.length})
            </h3>
          </div>
          <TaskList tasks={digestData?.overdue} emptyMessage="No overdue tasks" />
        </div>
      )}

      {/* Due Today */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Calendar" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">
            Due Today ({digestData?.dueToday?.length})
          </h3>
        </div>
        <TaskList tasks={digestData?.dueToday} emptyMessage="No tasks due today" />
      </div>

      {/* In Progress */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Clock" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">
            In Progress ({digestData?.inProgress?.length})
          </h3>
        </div>
        <TaskList tasks={digestData?.inProgress} emptyMessage="No tasks in progress" />
      </div>

      {/* Due Tomorrow */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="CalendarDays" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">
            Due Tomorrow ({digestData?.dueTomorrow?.length})
          </h3>
        </div>
        <TaskList tasks={digestData?.dueTomorrow} emptyMessage="No tasks due tomorrow" />
      </div>

      {/* This Week */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="CalendarRange" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">
            This Week ({digestData?.thisWeek?.length})
          </h3>
        </div>
        <TaskList tasks={digestData?.thisWeek} emptyMessage="No tasks due this week" />
      </div>

      {/* Completed Today */}
      {digestData?.completed?.length > 0 && (
        <div className="bg-card rounded-lg border border-success/20 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
            <h3 className="text-lg font-semibold text-foreground">
              Completed Today ({digestData?.completed?.length})
            </h3>
          </div>
          <TaskList tasks={digestData?.completed} emptyMessage="No tasks completed today" />
        </div>
      )}
    </div>
  );
};

export default DailyDigestView;